import React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Download, Upload, FileSpreadsheet, Loader2, Wrench } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BugReports } from '../components/admin/BugReports';
import { ImportCategoriesCard, NewCategoryForm, STAGE_SHORT } from '../components/admin/ImportCategoriesCard';
import { TargetUniverseCard } from '../components/admin/TargetUniverseCard';
import { invalidateImportCategories } from '../components/DealFilterPanel';
import { SendAuditCard } from '../components/admin/SendAuditCard';
import { NdaRefDocsCard } from '../components/admin/NdaRefDocsCard';
import { PrioConfigCard } from '../components/admin/PrioConfigCard';
import { IntakeFreezeCard } from '../components/IntakeFreezeCard';
import { MeetingSettingsCard } from '../components/MeetingSettingsCard';
import { MondayEmailCard } from '../components/MondayEmailCard';
import { EngagementSettingsCard } from '../components/EngagementSettingsCard';
import { AiSettingsCard } from '../components/AiSettingsCard';
import { AiCostDashboard } from '../components/AiCostDashboard';
import NotificationsPage from './NotificationsPage';

export default function SystemPage() {
  const [categories, setCategories] = useState([]);
  const [universeKey, setUniverseKey] = useState(0);
  const reloadCategories = useCallback(() => {
    api.listImportCategories().then(r => setCategories(r.data.categories || [])).catch(() => {});
  }, []);
  useEffect(() => { reloadCategories(); }, [reloadCategories]);
  const bumpUniverse = () => setUniverseKey(k => k + 1);
  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="system-page">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl text-foreground tracking-tight flex items-center gap-2.5">
          <Wrench className="w-7 h-7 text-blue-400" /> System
        </h1>
        <p className="text-muted-foreground text-sm mt-1">Integrations, data tools and platform administration.</p>
      </div>

      {/* Deal prio A/B/C — criteria texts, "?" definitions and the A/B rules */}
      <PrioConfigCard />

      {/* Team meeting quotas & finalize permission */}
      <MeetingSettingsCard />

      {/* Ask OLTA AI — model & usage limits */}
      <AiSettingsCard />

      {/* Ask OLTA AI — costs & audit log */}
      <AiCostDashboard />

      {/* Monday Update email (team-wide weekly digest) */}
      <MondayEmailCard />

      {/* Cold owner-engagement threshold */}
      <EngagementSettingsCard />

      {/* Pipeline intake freeze */}
      <IntakeFreezeCard />

      {/* Import & Export */}
      <ImportExportTools categories={categories} reloadCategories={reloadCategories} onImported={bumpUniverse} />

      {/* Import categories (upload buckets) + target-universe tracking */}
      <ImportCategoriesCard refreshKey={universeKey} onChanged={() => { reloadCategories(); bumpUniverse(); }} />
      <TargetUniverseCard refreshKey={universeKey} />

      {/* Data Management */}
      <AdminDataTools />

      {/* Email send audit — which instance sent what */}
      <SendAuditCard />

      {/* NDA review reference documents (IFA/PRA) */}
      <NdaRefDocsCard />

      {/* Bug Reports */}
      <BugReports />

      {/* Push notification audit */}
      <NotificationsPage />
    </div>
  );
}

function ImportExportTools({ categories, reloadCategories, onImported }) {
  const API_BASE = process.env.REACT_APP_BACKEND_URL;
  const [batchUploading, setBatchUploading] = useState(false);
  const [batchProgress, setBatchProgress] = useState(null);
  const [batchTarget, setBatchTarget] = useState('succession_possible');
  const [newCat, setNewCat] = useState(false);
  const activeCats = (categories || []).filter(c => !c.archived);
  const isCategory = !['ai_reviewed', 'succession_possible'].includes(batchTarget);
  const selectedCat = isCategory ? activeCats.find(c => c.id === batchTarget) : null;

  const summarize = ({ created, skipped, skipped_duplicates, category }) =>
    `${created} deal${created !== 1 ? 's' : ''} imported${category ? ` → Kategorie „${category.name}“` : ''}${skipped > 0 ? ` (${skipped} empty rows skipped)` : ''}${skipped_duplicates > 0 ? ` · ${skipped_duplicates} Duplikat${skipped_duplicates !== 1 ? 'e' : ''} übersprungen (Name existiert bereits)` : ''}`;

  // Big files (Markus, 25.10.2026: 14.6k rows) keep importing on the server — poll progress instead of holding the request open
  const pollBatch = (batchId, category) => new Promise((resolve, reject) => {
    const tick = async () => {
      try {
        const { data } = await api.getBatchUploadStatus(batchId);
        setBatchProgress({ processed: data.processed || 0, total: data.total_rows || 0, created: data.created || 0 });
        if (data.status === 'done') return resolve({ ...data, category });
        if (data.status === 'error') return reject(new Error(data.error || 'Import fehlgeschlagen'));
        setTimeout(tick, 1500);
      } catch (e) { reject(e); }
    };
    tick();
  });

  const handleBatchUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    setBatchUploading(true);
    setBatchProgress(null);
    try {
      const res = await api.batchUploadProjects(file, isCategory ? undefined : batchTarget, isCategory ? batchTarget : undefined);
      let result = res.data;
      if (result.status === 'running') {
        toast.info(`Große Datei (${(result.total || 0).toLocaleString('de-DE')} Zeilen) — der Import läuft im Hintergrund weiter, du kannst die Seite offen lassen.`);
        result = await pollBatch(result.batch_id, result.category);
      }
      toast.success(summarize(result), { duration: 8000 });
      invalidateImportCategories();
      reloadCategories && reloadCategories();
      onImported && onImported();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Batch upload failed', { duration: 8000 });
    }
    setBatchUploading(false);
    setBatchProgress(null);
  };

  return (
    <div className="bg-card border border-border rounded-sm p-6 space-y-4" data-testid="import-export-tools">
      <h2 className="font-heading text-lg text-foreground">Import & Export</h2>

      {/* Export */}
      <div className="space-y-2">
        <p className="text-[10px] text-muted-foreground/50 uppercase tracking-widest font-bold">Export</p>
        <a href={`${API_BASE}/api/export/all-deals`} target="_blank" rel="noopener noreferrer" data-testid="export-all-deals-btn">
          <Button className="bg-emerald-900/30 hover:bg-emerald-900/50 text-emerald-400 border border-emerald-900/30 rounded-sm font-medium text-sm h-9 px-4">
            <Download className="w-4 h-4 mr-1.5" /> Export All Deals
          </Button>
        </a>
      </div>

      {/* Batch Upload */}
      <div className="space-y-2 border-t border-border pt-4">
        <div className="flex items-center justify-between">
          <p className="text-[10px] text-muted-foreground/50 uppercase tracking-widest font-bold">Batch Upload</p>
          <a href={api.getBatchTemplate()} target="_blank" rel="noopener noreferrer" data-testid="batch-template-btn"
            className="text-[11px] text-muted-foreground hover:text-foreground transition-colors underline underline-offset-2">
            Download Template
          </a>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={batchTarget} onValueChange={setBatchTarget}>
            <SelectTrigger className="w-64 bg-secondary border-border rounded-sm h-9 text-xs" data-testid="batch-target-select"><SelectValue placeholder="Upload to..." /></SelectTrigger>
            <SelectContent className="bg-card border-border rounded-sm">
              <SelectItem value="ai_reviewed" className="text-xs">→ OLTA TAM (AI Reviewed)</SelectItem>
              <SelectItem value="succession_possible" className="text-xs">→ Succession Possible</SelectItem>
              {activeCats.length > 0 && <SelectItem value="__hdr" disabled className="text-[9px] text-muted-foreground font-bold">— Kategorien —</SelectItem>}
              {activeCats.map(c => (
                <SelectItem key={c.id} value={c.id} className="text-xs" data-testid={`batch-target-cat-${c.id}`}>
                  {c.name} <span className="text-muted-foreground">→ {STAGE_SHORT[c.stage]}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <label data-testid="batch-upload-btn">
            <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleBatchUpload} disabled={batchUploading} />
            <Button asChild className="bg-amber-900/30 hover:bg-amber-900/50 text-amber-400 border border-amber-900/30 rounded-sm font-medium text-sm h-9 px-4 cursor-pointer" disabled={batchUploading}>
              <span>
                {batchUploading
                  ? <><span className="w-3.5 h-3.5 border-2 border-amber-400/30 border-t-amber-400 rounded-full animate-spin mr-1.5" />
                      {batchProgress?.total ? `Importing… ${batchProgress.processed.toLocaleString('de-DE')} / ${batchProgress.total.toLocaleString('de-DE')}` : 'Importing...'}</>
                  : <><FileSpreadsheet className="w-4 h-4 mr-1.5" /> Add Batch</>}
              </span>
            </Button>
          </label>
          {batchUploading && batchProgress?.total > 0 && (
            <div className="flex items-center gap-2 w-full sm:w-72" data-testid="batch-progress">
              <div className="flex-1 h-1.5 bg-secondary rounded-full overflow-hidden">
                <div className="h-full bg-amber-400 transition-[width] duration-500" style={{ width: `${Math.min(100, Math.round(batchProgress.processed / batchProgress.total * 100))}%` }} />
              </div>
              <span className="text-[10px] font-mono text-muted-foreground whitespace-nowrap" data-testid="batch-progress-text">
                {Math.min(100, Math.round(batchProgress.processed / batchProgress.total * 100))}% · {batchProgress.created.toLocaleString('de-DE')} neu
              </span>
            </div>
          )}
          {!newCat && (
            <button onClick={() => setNewCat(true)} data-testid="batch-new-category-btn"
              className="text-[11px] text-blue-400 hover:text-blue-300 underline underline-offset-2">+ Neue Kategorie</button>
          )}
        </div>
        {newCat && (
          <NewCategoryForm compact onCancel={() => setNewCat(false)}
            onCreated={(c) => { setNewCat(false); setBatchTarget(c.id); reloadCategories && reloadCategories(); }} />
        )}
        <p className="text-[10px] text-muted-foreground" data-testid="batch-target-hint">
          {selectedCat
            ? `Deals werden mit der Kategorie „${selectedCat.name}“ markiert und landen in ${STAGE_SHORT[selectedCat.stage]} (${selectedCat.deal_count || 0} Deals bisher).`
            : 'Ohne Kategorie landen die Deals direkt in der gewählten Stufe — mit einer Kategorie bleiben sie im Explorer filterbar und das Universe-Wachstum ist nachvollziehbar.'}
        </p>
      </div>
    </div>
  );
}

function AdminDataTools() {
  const API_BASE = process.env.REACT_APP_BACKEND_URL;
  const [restoring, setRestoring] = React.useState(false);
  const [deleteStep, setDeleteStep] = React.useState(0); // 0=none, 1=warning1, 2=warning2, 3=totp
  const [deleteConfirm, setDeleteConfirm] = React.useState('');
  const [totpCode, setTotpCode] = React.useState('');
  const [deleting, setDeleting] = React.useState(false);
  // Stage delete
  const [stageDeleteStage, setStageDeleteStage] = React.useState('');
  const [stageDeleteTotp, setStageDeleteTotp] = React.useState('');
  const [stageDeleting, setStageDeleting] = React.useState(false);
  const [stageDeleteStep, setStageDeleteStep] = React.useState(0); // 0=select, 1=totp

  const handleDeleteStage = async () => {
    if (!stageDeleteStage || stageDeleteTotp.length < 6) return;
    setStageDeleting(true);
    try {
      const res = await api.deleteStagDeals(stageDeleteStage, stageDeleteTotp);
      toast.success(res.data.message);
      setStageDeleteStep(0);
      setStageDeleteStage('');
      setStageDeleteTotp('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setStageDeleting(false);
  };

  const handleRestore = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setRestoring(true);
    try {
      const res = await api.restoreBackup(file);
      toast.success(`Restore complete: ${JSON.stringify(res.data.restored)}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Restore failed'); }
    setRestoring(false);
    e.target.value = '';
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res = await api.deleteAllDeals(deleteConfirm, totpCode);
      toast.success(res.data.message);
      setDeleteStep(0);
      setDeleteConfirm('');
      setTotpCode('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setDeleting(false);
  };

  return (
    <div className="bg-card border border-border rounded-sm p-6 space-y-6">
      <h2 className="font-heading text-lg text-foreground">Data Management</h2>

      {/* Funnel Overrides */}
      <FunnelOverridesEditor />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {/* Backup */}
        <a href={`${API_BASE}/api/admin/backup`} target="_blank" rel="noopener noreferrer">
          <Button className="w-full bg-blue-600 hover:bg-blue-500 text-white rounded-sm text-xs h-9">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Download Full Backup
          </Button>
        </a>

        {/* Restore */}
        <label className="cursor-pointer">
          <Button disabled={restoring} className="w-full bg-secondary hover:bg-secondary/80 text-foreground border border-border rounded-sm text-xs h-9" asChild>
            <span>{restoring ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Restoring...</> : <><Upload className="w-3.5 h-3.5 mr-1.5" /> Restore from Backup</>}</span>
          </Button>
          <input type="file" accept=".json" className="hidden" onChange={handleRestore} />
        </label>

        {/* Delete All */}
        {deleteStep === 0 ? (
          <Button onClick={() => setDeleteStep(1)} variant="outline" className="w-full border-red-800/30 text-red-400 hover:bg-red-900/10 rounded-sm text-xs h-9">
            Delete All Deals
          </Button>
        ) : deleteStep === 1 ? (
          <div className="space-y-2">
            <p className="text-xs text-red-400 font-medium">Are you sure? This cannot be undone!</p>
            <div className="flex gap-1">
              <Button onClick={() => setDeleteStep(2)} size="sm" className="bg-red-600 hover:bg-red-500 text-white text-[10px] h-7">Yes, continue</Button>
              <Button onClick={() => setDeleteStep(0)} size="sm" variant="ghost" className="text-[10px] h-7">Cancel</Button>
            </div>
          </div>
        ) : deleteStep === 2 ? (
          <div className="space-y-2">
            <p className="text-xs text-red-400">Type <strong>DELETE ALL DEALS</strong> to confirm:</p>
            <Input value={deleteConfirm} onChange={e => setDeleteConfirm(e.target.value)}
              className="bg-secondary border-border rounded-sm h-7 text-xs font-mono" />
            <div className="flex gap-1">
              <Button onClick={() => deleteConfirm === 'DELETE ALL DEALS' ? setDeleteStep(3) : toast.error('Type the confirmation text')}
                size="sm" className="bg-red-600 hover:bg-red-500 text-white text-[10px] h-7">Next</Button>
              <Button onClick={() => { setDeleteStep(0); setDeleteConfirm(''); }} size="sm" variant="ghost" className="text-[10px] h-7">Cancel</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-red-400">Enter your authenticator code:</p>
            <Input value={totpCode} onChange={e => setTotpCode(e.target.value)}
              placeholder="6-digit code" maxLength={6}
              className="bg-secondary border-border rounded-sm h-7 text-xs font-mono" />
            <div className="flex gap-1">
              <Button onClick={handleDelete} disabled={deleting || totpCode.length < 6}
                size="sm" className="bg-red-600 hover:bg-red-500 text-white text-[10px] h-7">
                {deleting ? 'Deleting...' : 'Delete Everything'}
              </Button>
              <Button onClick={() => { setDeleteStep(0); setTotpCode(''); }} size="sm" variant="ghost" className="text-[10px] h-7">Cancel</Button>
            </div>
          </div>
        )}
      </div>

      {/* Delete by Stage */}
      <div className="border border-border rounded-sm p-4 space-y-3">
        <p className="text-[10px] text-muted-foreground/50 uppercase tracking-widest font-bold">Delete Deals by Stage</p>
        {stageDeleteStep === 0 ? (
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={stageDeleteStage} onValueChange={setStageDeleteStage}>
              <SelectTrigger className="w-48 bg-secondary border-border rounded-sm h-8 text-xs"><SelectValue placeholder="Select stage..." /></SelectTrigger>
              <SelectContent className="bg-card border-border rounded-sm">
                <SelectItem value="succession_possible" className="text-xs">Succession Possible</SelectItem>
                <SelectItem value="ai_reviewed" className="text-xs">OLTA TAM (AI Reviewed)</SelectItem>
                <SelectItem value="olta_reviewed" className="text-xs">Under OLTA Review</SelectItem>
                <SelectItem value="orbis_total" className="text-xs">Orbis Total</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={() => stageDeleteStage ? setStageDeleteStep(1) : toast.error('Select a stage first')}
              variant="outline" className="border-red-800/30 text-red-400 hover:bg-red-900/10 rounded-sm text-xs h-8 px-3">
              Delete Stage Deals
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-red-400">Delete all deals in <strong>{stageDeleteStage}</strong>. Enter authenticator code:</p>
            <div className="flex items-center gap-2">
              <Input value={stageDeleteTotp} onChange={e => setStageDeleteTotp(e.target.value)}
                placeholder="6-digit code" maxLength={6}
                className="bg-secondary border-border rounded-sm h-7 text-xs font-mono w-32" />
              <Button onClick={handleDeleteStage} disabled={stageDeleting || stageDeleteTotp.length < 6}
                size="sm" className="bg-red-600 hover:bg-red-500 text-white text-[10px] h-7">
                {stageDeleting ? 'Deleting...' : 'Confirm Delete'}
              </Button>
              <Button onClick={() => { setStageDeleteStep(0); setStageDeleteTotp(''); }} size="sm" variant="ghost" className="text-[10px] h-7">Cancel</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function FunnelOverridesEditor() {
  const [orbis, setOrbis] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);

  React.useEffect(() => {
    api.getFunnelStats().then(res => {
      const ov = res.data.overrides || {};
      setOrbis(String(ov.orbis_total || ''));
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.setFunnelOverrides({ orbis_total: parseInt(orbis) || 0 });
      toast.success('Funnel numbers updated');
    } catch (err) { toast.error('Failed to save'); }
    setSaving(false);
  };

  if (!loaded) return null;

  return (
    <div>
      <p className="text-[10px] text-muted-foreground/50 uppercase tracking-widest font-bold mb-2">Funnel Static Numbers</p>
      <div className="grid grid-cols-1 gap-3" style={{maxWidth: '300px'}}>
        <div>
          <p className="text-[10px] text-muted-foreground mb-1">Orbis Total</p>
          <Input type="number" value={orbis} onChange={e => setOrbis(e.target.value)}
            className="bg-secondary border-border rounded-sm h-8 text-sm font-mono" />
        </div>
      </div>
      <Button onClick={handleSave} disabled={saving} size="sm" className="mt-2 bg-blue-600 hover:bg-blue-500 text-white rounded-sm text-xs h-7 px-3">
        {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null} Save Numbers
      </Button>
    </div>
  );
}
